import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import styledComponent from '../../../utils/styledComponent';
import theme from '../../../theme';

// Side panel that collapses to a slim rail — right-hand by default, `side`
// flips the chevrons for a left-hand panel. Open state persists per
// `storageKey` in localStorage (default open); storage failures fall back to
// in-memory state. Feature-local to the Monitoring tab — promote to
// components/ only when a second page needs it.

function readStoredOpen(storageKey) {
  try {
    const stored = window.localStorage.getItem(storageKey);
    return stored === null ? true : stored === 'true';
  } catch {
    return true;
  }
}

const StyledAside = styledComponent('aside', {
  display: 'flex',
  flexDirection: 'column',
  background: theme.colors.bgCard,
  borderRadius: theme.radii.card,
  border: theme.borders.card,
  boxShadow: theme.shadows.card,
  overflow: 'hidden',
  transition: theme.transitions.default,

  '&.csp-open': { width: theme.sizes.monitoringPanelWidth },
  '&.csp-closed': { width: theme.sizes.monitoringPanelRailWidth },

  '& .csp-header': {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '8px',
    padding: '12px 12px 0 16px',
  },
  '& .csp-title': {
    fontSize: theme.sizes.headingFontSize,
    fontWeight: theme.fontWeights.semibold,
    color: theme.colors.textDark,
  },
  '& .csp-count': {
    marginLeft: '6px',
    fontSize: theme.sizes.smallFontSize,
    color: theme.colors.textMuted,
    fontVariantNumeric: 'tabular-nums',
  },
  '& .csp-toggle': {
    width: '28px',
    height: '28px',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: `1px solid ${theme.colors.borderLight}`,
    borderRadius: theme.radii.input,
    background: theme.colors.bgCard,
    color: theme.colors.textMuted,
    cursor: 'pointer',
    fontSize: theme.sizes.bodyFontSize,
    lineHeight: 1,
  },
  '& .csp-toggle:hover': {
    background: theme.colors.bgMuted,
    color: theme.colors.textDark,
  },
  '& .csp-toggle:focus-visible': {
    outline: 'none',
    borderColor: theme.colors.infoBlue,
    boxShadow: `0 0 0 2px ${theme.colors.infoBlue}33`,
  },
  // The body is a positioned box the children fill absolutely, so a long
  // card list scrolls inside the panel instead of growing the map row.
  '& .csp-body': {
    position: 'relative',
    flex: 1,
    minHeight: 0,
  },
  '& .csp-body-fill': {
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    padding: '12px 8px 8px',
    display: 'flex',
    flexDirection: 'column',
  },
  '& .csp-rail': {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '12px',
    padding: '12px 0',
  },
  '& .csp-rail-label': {
    writingMode: 'vertical-rl',
    fontSize: theme.sizes.captionFontSize,
    fontWeight: theme.fontWeights.semibold,
    color: theme.colors.textMuted,
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
  },
});

// The chevron points where the panel goes when clicked: a right panel
// collapses rightward ('›' while open), a left one leftward ('‹').
const CHEVRONS = {
  right: { collapse: '›', expand: '‹' },
  left: { collapse: '‹', expand: '›' },
};

function CollapsibleSidePanel({ title, count, storageKey, side, children }) {
  const [isOpen, setIsOpen] = useState(() => readStoredOpen(storageKey));
  const chevrons = CHEVRONS[side];

  useEffect(() => {
    try {
      window.localStorage.setItem(storageKey, String(isOpen));
    } catch {
      // Storage unavailable (private mode, quota): keep in-memory state only.
    }
  }, [isOpen, storageKey]);

  const toggle = (
    <button
      type="button"
      className="csp-toggle"
      aria-expanded={isOpen}
      aria-label={isOpen ? `Collapse ${title}` : `Expand ${title}`}
      onClick={() => setIsOpen((prev) => !prev)}
    >
      {isOpen ? chevrons.collapse : chevrons.expand}
    </button>
  );

  return (
    <StyledAside
      className={isOpen ? 'csp-open' : 'csp-closed'}
      data-testid="collapsible-side-panel"
    >
      {isOpen ? (
        <>
          <div className="csp-header">
            <div>
              <span className="csp-title">{title}</span>
              <span className="csp-count">{count}</span>
            </div>
            {toggle}
          </div>
          <div className="csp-body">
            <div className="csp-body-fill">{children}</div>
          </div>
        </>
      ) : (
        <div className="csp-rail">
          {toggle}
          <span className="csp-rail-label">{`${title} · ${count}`}</span>
        </div>
      )}
    </StyledAside>
  );
}

CollapsibleSidePanel.propTypes = {
  title: PropTypes.string.isRequired,
  count: PropTypes.number.isRequired,
  storageKey: PropTypes.string.isRequired,
  side: PropTypes.oneOf(Object.keys(CHEVRONS)),
  children: PropTypes.node.isRequired,
};

CollapsibleSidePanel.defaultProps = {
  side: 'right',
};

export default CollapsibleSidePanel;
